import { Link } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import {
  ArrowRightIcon,
  Button,
  EmptyState,
  ExternalLinkIcon,
  LockIcon,
} from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { buildStartPageEditorUrl } from '~publish/helpers/externalUrls'

import type { JSX } from 'react'

const GetStartPagePublishStatusForComments = graphql(/* GraphQL */ `
  query GetStartPagePublishStatusForComments(
    $organizationId: String!
    $pageId: String
  ) {
    startPage(organizationId: $organizationId, pageId: $pageId) {
      ... on StartPage {
        id
        isPublished
      }
      ... on CommonError {
        message
      }
    }
  }
`)

export const ChannelLockedError = ({
  channel,
}: {
  channel: {
    id: string
    service: string
  }
}): JSX.Element => {
  const isStartPage = channel.service === 'startPage'
  const organizationId = useOrganizationId()

  const { data, loading } = useQuery(GetStartPagePublishStatusForComments, {
    variables: {
      organizationId: organizationId ?? '',
      pageId: channel.id,
    },
    skip: !isStartPage || !organizationId,
  })

  const isUnpublishedStartPage =
    isStartPage &&
    !loading &&
    data?.startPage?.__typename === 'StartPage' &&
    !data.startPage.isPublished

  if (isStartPage && loading) {
    return (
      <EmptyState size="large" variant="warning">
        <EmptyState.Icon>
          <LockIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Loading...</EmptyState.Heading>
      </EmptyState>
    )
  }

  if (isUnpublishedStartPage) {
    return (
      <EmptyState size="large" variant="warning">
        <EmptyState.Icon>
          <LockIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>This Start Page is unpublished</EmptyState.Heading>
        <EmptyState.Description>
          Publish your Start Page from the Editor to start scheduling posts.
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button
            as="a"
            href={buildStartPageEditorUrl(channel.id)}
            target="_blank"
            rel="noopener noreferrer"
            size="large"
          >
            Open Editor <ExternalLinkIcon />
          </Button>
          <Button as={Link} to="/comments" size="large" variant="tertiary">
            <ArrowRightIcon />
            Go back to Community
          </Button>
        </EmptyState.Actions>
      </EmptyState>
    )
  }

  return (
    <EmptyState size="large" variant="warning">
      <EmptyState.Icon>
        <LockIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>This channel is locked</EmptyState.Heading>
      <EmptyState.Description>
        Channel you are trying to access is locked. Likely it is because your
        account was downgraded.
      </EmptyState.Description>
      <EmptyState.Actions>
        <Button as={Link} to="/comments" size="large">
          <ArrowRightIcon />
          Go back to Community
        </Button>
      </EmptyState.Actions>
    </EmptyState>
  )
}
